import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { map, Observable, of } from "rxjs";
import { environment } from "../../../../environments/environment";

const CDN_MASTERDATA_BASE = "https://cdn.lokos.in/lokos-masterdata/";

@Injectable({ providedIn: "root" })
export class ProfilesService {
  constructor(private http: HttpClient) {}

  getClfBasicDetailsByCode(clfCode: string): Observable<any> {
    return this.getClfBasicDetailsList({ page: 0, size: 1, clfCode }).pipe(
      map((res: any) => ({
        ...res,
        data: Array.isArray(res?.data?.content) ? res.data.content[0] ?? null : null,
      }))
    );
  }

  getClfBasicDetailsList(query: {
    page: number;
    size: number;
    stateId?: number | string;
    districtId?: number | string;
    blockId?: number | string;
    clfCode?: string;
    approvalStatus?: string;
    clfStatus?: string;
    moduleName?: string;
    sortBy?: string;
    sortDirection?: string;
  }): Observable<any> {
    const url = `${environment.clfApiBaseUrl}api/clf-profile/v1/clf-profile/basic`;
    let params = new HttpParams()
      .set("page", String(query.page))
      .set("size", String(query.size))
      .set("sortBy", query.sortBy || "createdOn")
      .set("sortDirection", query.sortDirection || "DESC");
    if (query.stateId != null && query.stateId !== "") {
      params = params.set("stateId", String(query.stateId));
    }
    if (query.districtId != null && query.districtId !== "") {
      params = params.set("districtId", String(query.districtId));
    }
    if (query.blockId != null && query.blockId !== "") {
      params = params.set("blockId", String(query.blockId));
    }
    if (query.clfCode) {
      params = params.set("clfCode", query.clfCode);
    }
    if (query.approvalStatus) {
      params = params.set("approvalStatus", query.approvalStatus);
    }
    if (query.clfStatus) {
      params = params.set("clfStatus", query.clfStatus);
    }
    params = params.set("moduleName", query.moduleName || "basic-profile");
    return this.http.get(url, { params, observe: "body" });
  }

  getGeoStateOptions(): Observable<Array<{ value: string; label: string }>> {
    return this.http.get<any>(`${CDN_MASTERDATA_BASE}statemaster.json?v=2`).pipe(
      map((res: any) => {
        const items = Array.isArray(res) ? res : [];
        return items.map((st: any) => ({
          value: `${st?.state_id ?? ""}`,
          label: `${st?.state_name_en ?? ""}` || `${st?.state_id ?? ""}`,
        }));
      })
    );
  }

  getGeoDistrictOptions(stateId: string | number): Observable<Array<{ value: string; label: string }>> {
    const padded = `${stateId}`.padStart(2, "0");
    return this.http.get<any>(`${CDN_MASTERDATA_BASE}state/${padded}.json?v=2`).pipe(
      map((res: any) => {
        const items = Array.isArray(res) ? res : [];
        return items.map((d: any) => ({
          value: `${d?.district_id ?? ""}`,
          label: `${d?.district_name_en ?? ""}` || `${d?.district_id ?? ""}`,
        }));
      })
    );
  }

  getGeoBlockOptions(stateId?: string | number, districtId?: string | number): Observable<Array<{ value: string; label: string }>> {
    if (stateId == null || stateId === "" || districtId == null || districtId === "") {
      return of([]);
    }
    const padded = `${stateId}`.padStart(2, "0");
    const url = `${CDN_MASTERDATA_BASE}state/${padded}/district/${districtId}.json?v=2`;
    return this.http.get<any>(url).pipe(
      map((res: any) => {
        const items = Array.isArray(res) ? res : [];
        const seen = new Set<string>();
        const options: Array<{ value: string; label: string }> = [];
        items.forEach((b: any) => {
          const id = `${b?.block_id ?? b?.blockId ?? ""}`;
          const name = `${b?.block_name_en ?? b?.block_name ?? b?.blockName ?? ""}`;
          if (!id || seen.has(id)) {
            return;
          }
          seen.add(id);
          options.push({ value: id, label: name || id });
        });
        options.sort((a, b) => a.label.localeCompare(b.label));
        return options;
      })
    );
  }

  getClfUserMappingByUserId(userId: string): Observable<any> {
    const url = `${environment.clfApiBaseUrl}api/clf-profile/v1/clf-profile/clf-user-mapping`;
    return this.http.get(url, { params: { userId }, observe: "body" });
  }

  updateBasicDetails(clfCode: string, body: any): Observable<any> {
    const url = `${environment.clfApiBaseUrl}api/clf-profile/v1/clf-profile/basic/${clfCode}`;
    const params = new HttpParams().set("moduleName", "basic-profile");
    return this.http.put(url, body, { params, observe: "body" });
  }

  modifyApprovalStatusBasicDetails(clfCode: string, body: any): Observable<any> {
    const url = `${environment.clfApiBaseUrl}api/clf-profile/v1/clf-profile/basic/${clfCode}/status`;
    const params = new HttpParams().set("moduleName", "basic-profile");
    return this.http.put(url, body, { params, observe: "body" });
  }

  getFormUpdateHistory(clfCode: string, moduleName = "basic-profile"): Observable<any> {
    const url = `${environment.clfApiBaseUrl}api/clf-profile/v1/clf-profile/history/${clfCode}`;
    const params = new HttpParams().set("moduleName", moduleName);
    return this.http.get(url, { params, observe: "body" });
  }

  getProfileStatusCount(query: {
    stateId?: number | string;
    districtId?: number | string;
    blockId?: number | string;
    clfCode?: string;
    clfStatus?: string;
  }): Observable<any> {
    const url = `${environment.clfApiBaseUrl}api/clf-profile/v1/clf-profile/status-count`;
    let params = new HttpParams();
    if (query.stateId != null && query.stateId !== "") {
      params = params.set("stateId", String(query.stateId));
    }
    if (query.districtId != null && query.districtId !== "") {
      params = params.set("districtId", String(query.districtId));
    }
    if (query.blockId != null && query.blockId !== "") {
      params = params.set("blockId", String(query.blockId));
    }
    if (query.clfCode) {
      params = params.set("clfCode", query.clfCode);
    }
    if (query.clfStatus) {
      params = params.set("clfStatus", query.clfStatus);
    }
    return this.http.get(url, { params, observe: "body" });
  }

  getProfileLastUpdated(query: {
    stateId?: number | string;
    districtId?: number | string;
    blockId?: number | string;
    clfCode?: string;
  }): Observable<any> {
    const url = `${environment.clfApiBaseUrl}api/clf-profile/v1/clf-profile/status-count/last-updated`;
    let params = new HttpParams();
    if (query.stateId != null && query.stateId !== "") {
      params = params.set("stateId", String(query.stateId));
    }
    if (query.districtId != null && query.districtId !== "") {
      params = params.set("districtId", String(query.districtId));
    }
    if (query.blockId != null && query.blockId !== "") {
      params = params.set("blockId", String(query.blockId));
    }
    if (query.clfCode) {
      params = params.set("clfCode", query.clfCode);
    }
    return this.http.get(url, { params, observe: "body" });
  }
}
